import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowLeft, Trash2, ShoppingCart as ShoppingCartIcon, Video as VideoIcon, Image as ImageIcon } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import GalleryThemeToggle from "../components/GalleryThemeToggle";
import ShopFooter from "../components/ShopFooter";
import { PublicVideoThumbnail } from "../components/PublicVideoThumbnail";
import { toast } from "sonner";
import { ShopLanguageSelector } from "../components/ShopLanguageSelector";
import { DEFAULT_CURRENCY } from "@shared/const";

interface GallerySession {
  projectId: number;
  projectTitle: string;
  cartId: number;
  token: string;
  photographerId?: number;
}

export default function GalleryCart() {
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const [session, setSession] = useState<GallerySession | null>(null);

  useEffect(() => {
    const sessionData = localStorage.getItem("gallery_session");
    if (!sessionData) {
      navigate("/gallery/login");
      return;
    }

    try {
      const parsed = JSON.parse(sessionData);
      setSession(parsed);
    } catch (error) {
      navigate("/gallery/login");
    }
  }, [navigate]);

  const { data: cartData, refetch } = trpc.gallery.getCart.useQuery(
    {
      cartId: session?.cartId || 0,
      token: session?.token || "",
    },
    {
      enabled: !!session,
    }
  );

  const photographerId = session?.photographerId;
  const { data: settings } = trpc.settings.getPublicPayPalSettings.useQuery(
    { photographerId: photographerId || 0 },
    { enabled: !!photographerId }
  );

  const removeFromCartMutation = trpc.gallery.removeFromCart.useMutation({
    onSuccess: () => {
      refetch();
      toast.success(t.cart.itemRemoved);
    },
  });

  const handleRemove = (cartItemId: number) => {
    if (!session) return;
    
    removeFromCartMutation.mutate({
      cartItemId,
      token: session.token,
    });
  };

  const handleCheckout = () => {
    navigate("/gallery/checkout");
  };

  if (!session) {
    return null;
  }

  const items = cartData?.items || [];
  const total = items.reduce((sum, item) => {
    const price = parseFloat(item.priceAtAdd || "0");
    return sum + price * (item.quantity || 1);
  }, 0);
  const currency = settings?.currency || DEFAULT_CURRENCY;
  const formatAmount = (value: number) => `${value.toFixed(2)} ${currency}`;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2 md:gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate("/gallery")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t.common.back}
              </Button>
              <BrandLogo size={32} rounded showName className="md:block" />
              <div>
                <h1 className="text-lg md:text-2xl font-bold">{t.cart.title}</h1>
                <p className="text-xs md:text-sm text-muted-foreground">{session.projectTitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end md:justify-start">
              <ShopLanguageSelector />
              {session && <GalleryThemeToggle cartId={session.cartId} token={session.token} />}
            </div>
          </div>
        </div>
      </header>

      {/* Cart Content */}
      <main className="container mx-auto px-4 py-8 flex-1">
        {items.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <ShoppingCartIcon className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">{t.cart.emptyCart}</p>
              <Button onClick={() => navigate("/gallery")}>
                {t.cart.continueShopping}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t.cart.title}</CardTitle>
                  <CardDescription>
                    {items.length} {items.length === 1 ? t.cart.item : t.cart.items}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Desktop Table View */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>{t.common.quantity}</TableHead>
                          <TableHead>{t.common.price}</TableHead>
                          <TableHead className="text-right">{t.common.actions}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item) => {
                          const isVideo = item.itemType === "video";
                          const filename = isVideo ? item.videoFilename : item.imageFilename;
                          const fileSize = isVideo && item.videoFileSize 
                            ? `${(item.videoFileSize / (1024 * 1024)).toFixed(2)} MB` 
                            : null;
                          
                          // Product type details
                          const productType = item.productType || "digital";
                          const printFormat = item.printFormat;

                          return (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  {isVideo ? (
                                    <div className="w-16 h-16 rounded overflow-hidden">
                                      <PublicVideoThumbnail
                                        videoId={item.videoId!}
                                        token={session.token}
                                        alt={filename || "video"}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ) : item.imageId ? (
                                    <img
                                      src={`/api/image-proxy/${item.imageId}/thumbnail`}
                                      alt={filename || ""}
                                      className="w-16 h-16 object-cover rounded"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-sm font-medium">{filename}</p>
                                    {fileSize && <p className="text-xs text-muted-foreground">{fileSize}</p>}
                                    {!isVideo && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {productType === "print" ? `${t.common.print}: ${printFormat}` : t.common.digital}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {isVideo ? (
                                    <>
                                      <VideoIcon className="h-4 w-4" />
                                      <span className="text-sm">Video</span>
                                    </>
                                  ) : (
                                    <>
                                      <ImageIcon className="h-4 w-4" />
                                      <span className="text-sm">
                                        {productType === "print" ? t.common.print : t.common.digital}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>
                                {formatAmount(parseFloat(item.priceAtAdd || "0"))}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRemove(item.id!)}
                                  disabled={removeFromCartMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-4">
                    {items.map((item) => {
                      const isVideo = item.itemType === "video";
                      const filename = isVideo ? item.videoFilename : item.imageFilename;
                      const fileSize = isVideo && item.videoFileSize 
                        ? `${(item.videoFileSize / (1024 * 1024)).toFixed(2)} MB` 
                        : null;
                      
                      // Product type details
                      const productType = item.productType || "digital";
                      const printFormat = item.printFormat;

                      return (
                        <Card key={item.id}>
                          <CardContent className="p-4">
                            <div className="flex gap-3">
                              {/* Thumbnail */}
                              <div className="flex-shrink-0">
                                {isVideo ? (
                                  <div className="w-20 h-20 rounded overflow-hidden">
                                    <PublicVideoThumbnail
                                      videoId={item.videoId!}
                                      token={session.token}
                                      alt={filename || "video"}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : item.imageId ? (
                                  <img
                                    src={`/api/image-proxy/${item.imageId}/thumbnail`}
                                    alt={filename || ""}
                                    className="w-20 h-20 object-cover rounded"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-20 h-20 bg-muted rounded flex items-center justify-center">
                                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                                  </div>
                                )}
                              </div>

                              {/* Details */}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{filename}</p>
                                {fileSize && <p className="text-xs text-muted-foreground">{fileSize}</p>}
                                <div className="flex items-center gap-1 mt-1">
                                  {isVideo ? (
                                    <>
                                      <VideoIcon className="h-3 w-3" />
                                      <span className="text-xs text-muted-foreground">Video</span>
                                    </>
                                  ) : (
                                    <>
                                      <ImageIcon className="h-3 w-3" />
                                      <span className="text-xs text-muted-foreground">
                                        {productType === "print" ? `${t.common.print}: ${printFormat}` : t.common.digital}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs text-muted-foreground">Menge: {item.quantity}</span>
                                    <span className="font-semibold">{formatAmount(parseFloat(item.priceAtAdd || "0"))}</span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRemove(item.id!)}
                                    disabled={removeFromCartMutation.isPending}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>{t.checkout.orderSummary}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span>{t.cart.subtotal}</span>
                    <span>{formatAmount(total)}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between font-bold text-lg">
                      <span>{t.cart.total}</span>
                      <span>{formatAmount(total)}</span>
                    </div>
                  </div>
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleCheckout}
                    disabled={items.length === 0}
                  >
                    {t.cart.proceedToCheckout}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate("/gallery")}
                  >
                    {t.cart.continueShopping}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
      <ShopFooter />
    </div>
  );
}
